import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const UIContext = createContext(null);

export const useUI = () => useContext(UIContext);

const TOAST_ICONS = {
  error: FiIcons.FiAlertCircle,
  success: FiIcons.FiCheckCircle,
  info: FiIcons.FiInfo,
};

const TOAST_TONE = {
  error: 'text-red-600 dark:text-red-400',
  success: 'text-emerald-600 dark:text-emerald-400',
  info: 'text-primary-600 dark:text-primary-400',
};

export const UIProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const resolverRef = useRef(null);
  const idRef = useRef(0);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => dismissToast(id), duration);
    }
  }, [dismissToast]);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfirmState({
        message,
        title: options.title || '確認',
        confirmLabel: options.confirmLabel || 'OK',
        cancelLabel: options.cancelLabel || 'キャンセル',
        danger: !!options.danger,
      });
    });
  }, []);

  const handleConfirmResult = (result) => {
    setConfirmState(null);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  return (
    <UIContext.Provider value={{ toast, confirm }}>
      {children}

      {/* Toast stack */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-[calc(100%-2rem)] max-w-sm pointer-events-none">
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: -12, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 40, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 400, damping: 32 }}
              className="pointer-events-auto flex items-start gap-3 p-4 rounded-2xl backdrop-blur-2xl bg-white/85 dark:bg-gray-900/80 border border-white/60 dark:border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.18)]"
            >
              <SafeIcon icon={TOAST_ICONS[t.type] || FiIcons.FiInfo} className={`text-xl mt-0.5 shrink-0 ${TOAST_TONE[t.type] || TOAST_TONE.info}`} />
              <p className="text-sm font-medium text-gray-900 dark:text-white leading-snug">{t.message}</p>
              <button
                onClick={() => dismissToast(t.id)}
                className="ml-auto shrink-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-base" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Confirm dialog */}
      <AnimatePresence>
        {confirmState && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4"
            onClick={() => handleConfirmResult(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 12 }}
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-3xl backdrop-blur-2xl bg-white/90 dark:bg-gray-900/85 border border-white/60 dark:border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.25)] overflow-hidden"
            >
              <div className="p-6 text-center">
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">{confirmState.title}</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{confirmState.message}</p>
              </div>
              <div className="grid grid-cols-2 border-t border-gray-200/70 dark:border-white/10">
                <button
                  onClick={() => handleConfirmResult(false)}
                  className="py-3.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/5 transition-colors border-r border-gray-200/70 dark:border-white/10"
                >
                  {confirmState.cancelLabel}
                </button>
                <button
                  onClick={() => handleConfirmResult(true)}
                  className={`py-3.5 text-sm font-bold transition-colors ${
                    confirmState.danger
                      ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                      : 'text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                  }`}
                >
                  {confirmState.confirmLabel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </UIContext.Provider>
  );
};